import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { InvoiceDetailHeatDto } from '../services/invoiceApi';
import {
  inventoryReportApiService,
  HeatNumberConsumptionReportDto,
} from '../services/inventoryReportApi';

export interface InvoiceRmHeatPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  branchId: number | null;
  rawItemId: number | null;
  storeId?: number | null;
  initialHeats: InvoiceDetailHeatDto[];
  disabled?: boolean;
  onConfirm: (heats: InvoiceDetailHeatDto[]) => void;
}

type HeatDraft = {
  heatNo: string;
  available: number;
  quantity: string;
  selected: boolean;
};

function sumHeatQuantities(heats: InvoiceDetailHeatDto[]): number {
  return heats.reduce((s, h) => s + (Number(h.quantity) || 0), 0);
}

export function InvoiceRmHeatPickerDialog({
  open,
  onOpenChange,
  branchId,
  rawItemId,
  storeId,
  initialHeats,
  disabled = false,
  onConfirm,
}: InvoiceRmHeatPickerDialogProps) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<HeatDraft[]>([]);

  useEffect(() => {
    if (!open || branchId == null || rawItemId == null) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        setLoading(true);
        const res = await inventoryReportApiService.getHeatNumberConsumptionByBranchAndRawItem(
          branchId,
          rawItemId
        );
        if (cancelled) return;
        const list = res.success && Array.isArray(res.data) ? res.data : [];
        const byHeat = new Map<string, HeatNumberConsumptionReportDto>();
        for (const row of list) {
          const heatNo = String(row.heatNo ?? '').trim();
          if (!heatNo) continue;
          const remaining = Number(row.remainingQuantity ?? 0);
          const existing = byHeat.get(heatNo);
          if (!existing || remaining > Number(existing.remainingQuantity ?? 0)) {
            byHeat.set(heatNo, row);
          }
        }

        const initialByHeat = new Map(
          initialHeats
            .filter((h) => String(h.heatNo ?? '').trim())
            .map((h) => [String(h.heatNo).trim(), h] as const)
        );

        const drafts: HeatDraft[] = [];
        byHeat.forEach((row, heatNo) => {
          const available = Math.max(0, Number(row.remainingQuantity ?? 0));
          const selected = initialByHeat.get(heatNo);
          if (available > 0 || selected) {
            drafts.push({
              heatNo,
              available,
              quantity: selected ? String(selected.quantity ?? '') : '',
              selected: Boolean(selected),
            });
          }
        });

        // Keep any already-selected heats that are no longer in availability (edit / stale)
        initialByHeat.forEach((heat, heatNo) => {
          if (!drafts.some((d) => d.heatNo === heatNo)) {
            drafts.push({
              heatNo,
              available: Number(heat.quantity) || 0,
              quantity: String(heat.quantity ?? ''),
              selected: true,
            });
          }
        });

        drafts.sort((a, b) => a.heatNo.localeCompare(b.heatNo));
        setRows(drafts);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, branchId, rawItemId, initialHeats]);

  const selectedTotal = useMemo(() => {
    return rows
      .filter((r) => r.selected)
      .reduce((s, r) => s + (Number(r.quantity) || 0), 0);
  }, [rows]);

  const handleConfirm = () => {
    const heats: InvoiceDetailHeatDto[] = [];
    for (const row of rows) {
      if (!row.selected) continue;
      const qty = Number(row.quantity);
      if (!row.heatNo || Number.isNaN(qty) || qty <= 0) {
        toast.error(`Enter a valid quantity for heat ${row.heatNo || '(blank)'}`);
        return;
      }
      if (qty > row.available + 1e-9 && row.available >= 0) {
        toast.error(`Heat ${row.heatNo}: qty ${qty} exceeds available ${row.available}`);
        return;
      }
      heats.push({
        heatNo: row.heatNo,
        quantity: qty,
        storeId: storeId ?? null,
      });
    }
    if (heats.length === 0) {
      toast.error('Select at least one heat with quantity');
      return;
    }
    onConfirm(heats);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select heats &amp; quantities</DialogTitle>
        </DialogHeader>

        {branchId == null || rawItemId == null ? (
          <p className="text-sm text-gray-600">Select a branch and raw item first.</p>
        ) : loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-7 w-7 animate-spin text-primary-600" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-600 py-6 text-center">
            No heats with remaining quantity for this raw item in the selected branch.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">
              Total selected qty:{' '}
              <span className="font-semibold text-primary-800">{selectedTotal.toLocaleString('en-IN')}</span>
            </div>
            <div className="overflow-x-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Use</TableHead>
                    <TableHead>Heat no</TableHead>
                    <TableHead className="text-right">Available</TableHead>
                    <TableHead className="text-right w-36">Qty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.heatNo}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={row.selected}
                          disabled={disabled}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setRows((prev) =>
                              prev.map((r) =>
                                r.heatNo === row.heatNo
                                  ? {
                                      ...r,
                                      selected: checked,
                                      quantity:
                                        checked && !r.quantity
                                          ? String(r.available || '')
                                          : r.quantity,
                                    }
                                  : r
                              )
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{row.heatNo}</TableCell>
                      <TableCell className="text-right">{row.available.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          disabled={disabled || !row.selected}
                          value={row.quantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            setRows((prev) =>
                              prev.map((r) =>
                                r.heatNo === row.heatNo ? { ...r, quantity: value, selected: true } : r
                              )
                            );
                          }}
                          className="h-8 text-right"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-gray-500">
              Invoice line quantity will be the sum of selected heat quantities.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={disabled || loading || rows.length === 0}>
            Apply heats
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { sumHeatQuantities };
